import * as React from 'react';
import autobind from 'autobind-decorator';
import { resolve } from 'inversify-react';

import { CardController } from 'src/app/shared/card.controller';
import { QuoteGeneralPartView } from './quote-general.view';
import { ObjectUtils } from 'src/core/utils/object';
import { ToastNotifications } from 'src/core/ui/toast';

interface QuoteGeneralPartControllerProps extends ComponentViewProps<QuoteItem> {
	contextService: any;
	mode: ControlMode;
	newQuotePax: (quote: QuoteItem, client?: ClientItem) => any;
	changeStatus: (cancel?: boolean) => () => void;
	paxes: QuotePaxItem[];
	updateFees: () => Promise<void>;
	updatePrice: (marketId?: number, bookingDate?: Date) => Promise<boolean>;
	depositPercent: number;
	dueDateInDays: number;
	onMarketChange: (selected: MarketSummary) => void;
	openInitialDeposit: boolean;
	onSubmitPayment: (paymentInfo: PaymentInfo, immediatelyPerformPayment: boolean, quoteId?: number) => Promise<void>;
	closeInitialDepositDialog: () => void;
	onAgentChange: (agent: AgentSummary) => Promise<void>;
	onPaymentModeChange: (paymentMode: number) => Promise<void>;
	currencyId: number;
	currencyCode: string;
}

export class QuoteGeneralPartController extends React.Component<QuoteGeneralPartControllerProps, {}> {
	@resolve
	private toast: ToastNotifications;

	@autobind
	private updateClient(quote: QuoteItem, client: ClientItem): void {
		if (client != null) {
			quote.clientId = client.id;
			quote.clientDisplayName = client.displayName;
			quote.clientType = client.type;
			let pax: QuotePaxItem = this.props.paxes.find(x => x.clientId == client.id);
			if (pax == null) {
				this.props.newQuotePax(quote, client);
			} else {
				pax.clientDisplayName = client.displayName;
				pax.clientId = client.id;
				const model = client.dynamicProperties['person'] as PersonItem;
				pax.dynamicProperties['salutation'] = model.title;
				pax.dynamicProperties['foreName'] = model.firstName;
				pax.dynamicProperties['middleName'] = model.middleName;
				pax.dynamicProperties['surName'] = model.lastName;
				pax.dynamicProperties['birthDate'] = new Date(model.birthDate);
				pax.dynamicProperties['gender'] = model.gender;
				pax.dynamicProperties['billingTo'] = quote.clientId;
			}
		} else {
			quote.clientId = null;
		}
	}

	@autobind
	private async handleChangeField(fieldName: string, requireUpdatePrice: boolean = false, requirePolicyChecking: boolean = false): Promise<void> {
		const { model, updateFees, updatePrice } = this.props;

		if (requirePolicyChecking && model.marketId != null) {
			await updateFees();
		}
		if (requireUpdatePrice && model.marketId != null) {
			await updatePrice(model.marketId, model.reservationDate);
		}
	}

	private async performAsync<T>(body: () => Promise<T>): Promise<T> {
		const { setBusy } = this.props;

		setBusy(true);
		try {
			const result = await body();
			return result;
		} catch (error) {
			this.toast.showToast('Request failed', ObjectUtils.parseError(error), 'error');
		} finally {
			setBusy(false);
		}
	}

	@autobind
	private async handleSubmitInitialDeposit(paymentInfo: PaymentInfo, immediatelyPerformPayment: boolean): Promise<void> {
		this.performAsync(async () => {
			const { model, onSubmitPayment, closeInitialDepositDialog } = this.props;

			await onSubmitPayment(paymentInfo, immediatelyPerformPayment, model.id);
			closeInitialDepositDialog();
		});
	}

	render(): React.ReactNode {
		const model = this.props.model;
		if (model == null) {
			return null;
		}

		return (
			<CardController
				className="card-borderless"
				mode={this.props.mode}>
				<QuoteGeneralPartView
					{...this.props}
					model={model}
					onChangeField={this.handleChangeField}
					updateClient={this.updateClient}
					onSubmitInitialDeposit={this.handleSubmitInitialDeposit}
				/>
			</CardController>
		);
	}
}



// WEBPACK FOOTER //
// ./src/app/bookings/quotes/form/parts/quote-general.controller.tsx